import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type CommunityTopicsByEngagementView = {
  /** `Top pairing` — the largest topic-and-channel cell of the drawn stack. */
  kpis: KPIMetric[];
  /** The top topics by engagement (6, or 4 in a narrow column), one key per channel. */
  rows: ChartRow[];
  xKey: string;
  /** The channels present in the community rows. */
  series: ChartSeries[];
  /** Whether any community row is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type CommunityTopicsByEngagementCardProps = WidgetBlockProps & {
  view?: CommunityTopicsByEngagementView;
  /** Opens the conversations behind a topic (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Community's last card (947:19575), and Overview's (972:21115).
 *
 * Engagement rather than mentions: the card asks what communities *react* to,
 * which is a different question — a topic can be mentioned constantly and
 * engaged with by nobody.
 *
 * Stacked columns rather than the prototype's grouped bars. The prototype
 * grouped four topics against four platforms; this corpus carries seven channels
 * and the community slice holds 158 rows across nineteen topics, so a grouped
 * plot of the top six would draw 42 bars over about 80 rows and most of them
 * would be missing. A stack has no gaps to show.
 *
 * How many topics it draws depends on its column (`span`) — the presenter
 * applies that cap; see `presentCommunityTopics`.
 */
export function CommunityTopicsByEngagementCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: CommunityTopicsByEngagementCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No community conversation in this period"
      {...cardState(status, hasData, onRetry)}
      title="Community topics by engagement"
      helpText="What communities are engaging with, and on which channel — the pairing of subject and place, rather than either on its own."
      helpSupportingText={`Engagement rather than mentions, so the chart shows where conversation actually happens rather than where the brand is merely named. The ${view?.rows.length ?? 0} topics carrying the most engagement in this period; the figure above names the single largest topic-and-channel pairing among them.`}
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} orientation="platforms" />}
    />
  );
}
